/**
 * Grabbing and dragging a note that already exists (#157).
 *
 * Split out of `NoteTool` because moving a note is not a note-authoring
 * gesture — it is the same "pick the thing up and put it somewhere else" the
 * Select tool does for every other object on the canvas. A maker who has just
 * placed a note and switched back to Select should not have to switch back
 * again to nudge the card out of the way, so both tools drive this one
 * controller rather than keeping two copies of the rules.
 *
 * Three grabs, told apart by what is under the cursor:
 *
 *  - the **numbered badge** pulls out a NEW callout: drag from it to a part
 *    and the note gains a line to it, keeping the ones it already had. It is
 *    the note's head, so it is where another line naturally starts;
 *  - a **tip** (the dot at the leather end of a leader) re-aims THAT callout:
 *    drop it on another part to point there, on bare canvas to remove that one
 *    line, leaving the rest;
 *  - **anything else about the note** — card, text, the leader's own body —
 *    moves the card.
 */
import type { EditorContext } from "./EditorContext";
import type { Note, Project, Vec2 } from "../model/Project";
import {
  MIN_NOTE_SIZE_MM,
  noteAnchorFor,
  noteAnchorTarget,
  noteAnchors,
  noteTargetKey,
  noteTargetPathData,
  resolveNoteAnchor,
  withNoteAnchors,
  type NoteTarget,
} from "../model/notes";
import {
  ANCHOR_DOT_RADIUS_MM,
  BADGE_LABEL_DY_MM,
  calloutLandingPoint,
  noteCardHeightMm,
} from "../canvas/noteRenderer";
import { pickLeatherObjectAt } from "./leatherPick";
import { cssVar } from "../colors";
import { focusNoteTextOnNextRender } from "../ui/notePanelSection";
import { ROW_DBLCLICK_MS } from "../ui/layersRowKit";
import { currentPointerKind, hitRadiusFor } from "../canvas/pointerPrecision";

export type NoteGrabKind = "move" | "anchor" | "connect" | "resize";

/** Which corner of the card a resize drag is holding. The OPPOSITE corner
 *  stays put, which is what makes a corner drag feel like a corner drag. */
export type NoteCorner = "nw" | "ne" | "se" | "sw";

export interface NoteGrab {
  kind: NoteGrabKind;
  noteId: string;
  /** Which callout the grab is on, for `"anchor"`. */
  anchorIndex?: number;
  /** Which corner is held, for `"resize"`. */
  corner?: NoteCorner;
}

/** How close to a corner counts as grabbing it, in project mm. Generous
 *  enough to hit with a mouse, small enough that the middle of a short card
 *  still moves rather than resizes. */
const CORNER_GRAB_MM = 3;

/** The card's drawn rectangle — its stored box, with the height the text has
 *  grown it to (#1074). */
function cardRect(
  note: Note,
  scope: paper.PaperScope,
): { left: number; top: number; right: number; bottom: number } {
  const h = noteCardHeightMm(note, scope);
  return {
    left: note.position.x,
    top: note.position.y,
    right: note.position.x + note.widthMm,
    bottom: note.position.y + h,
  };
}

/** Which corner of `note` the point is on, if any. */
function cornerAt(
  note: Note,
  p: Vec2,
  scope: paper.PaperScope,
): NoteCorner | null {
  const r = cardRect(note, scope);
  const near = (a: number, b: number) => Math.abs(a - b) <= CORNER_GRAB_MM;
  const l = near(p.x, r.left);
  const rt = near(p.x, r.right);
  const t = near(p.y, r.top);
  const b = near(p.y, r.bottom);
  if (t && l) return "nw";
  if (t && rt) return "ne";
  if (b && rt) return "se";
  if (b && l) return "sw";
  return null;
}

/** The two-headed arrow that says which way a corner drag will stretch. */
export function cursorForCorner(corner: NoteCorner): string {
  return corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize";
}

export class NoteDragController {
  private kind: NoteGrabKind | null = null;
  private noteId: string | null = null;
  /** Which of the note's callouts a tip drag is re-aiming. */
  private anchorIndex = 0;
  /** Which corner a resize drag is holding, and the card box it started from —
   *  every frame is measured against THAT, so the drag cannot drift by
   *  accumulating its own rounding. */
  private corner: NoteCorner | null = null;
  private resizeFrom: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null = null;
  /** Ephemeral leader drawn while a NEW callout is pulled out of the badge. */
  private previewLine: paper.Path | null = null;
  private previewDot: paper.Path | null = null;
  /** Ephemeral outline drawn around whatever the tip is currently over, and the
   *  target key it was built for — so it is rebuilt when the drag crosses onto
   *  something else and left alone on every other frame (#1076). */
  private highlight: paper.Item | null = null;
  private highlightKey: string | null = null;
  /** Pointer position at the last move, so a drag applies deltas rather than
   *  snapping the card's corner to the cursor. */
  private last: Vec2 | null = null;
  /** Where the grab started, so the release can commit one total offset. */
  private press: Vec2 | null = null;
  /** The project as it stood before the drag began. The live preview rewrites
   *  the current project without an undo entry, so this is what the single
   *  entry pushed on release has to be measured against. */
  private original: Project | null = null;
  /** The last note pressed, and when — so a second press on the same card
   *  reads as a double-click. Tracked here rather than from a `dblclick`
   *  event: the canvas is one element, so the browser's own double-click
   *  says nothing about WHICH note was hit twice. */
  private lastPress: { noteId: string; at: number } | null = null;

  constructor(private readonly ctx: EditorContext) {}

  /** True while a note drag is in flight — the host tool must not treat the
   *  same pointer stream as one of its own gestures. */
  get busy(): boolean {
    return this.kind !== null;
  }

  /**
   * The note part under a point, if any.
   *
   * Paper's own hit test is used rather than `Scene.hitTest` because that one
   * classifies leather/construction kinds and knows nothing about notes. The
   * anchor dot is checked before the card so the callout tip stays grabbable
   * even when it sits over its own card.
   */
  noteUnder(p: Vec2): NoteGrab | null {
    const scope = this.ctx.scene.getPaperScope();
    const hits = scope.project.hitTestAll(new scope.Point(p.x, p.y), {
      fill: true,
      stroke: true,
      tolerance: hitRadiusFor(4, currentPointerKind()) / scope.view.zoom,
    });
    let card: NoteGrab | null = null;
    for (const h of hits ?? []) {
      // Walk up: paper reports the enclosing GROUP for a hit inside a clipped
      // group, so matching only the leaf kinds missed the card entirely and
      // the note refused to be grabbed.
      for (let item = h.item; item; item = item.parent) {
        const data = item.data as
          | { kind?: string; noteId?: string; anchorIndex?: number }
          | undefined;
        if (!data?.noteId) continue;
        // Tip and badge both beat the card: they are the small, deliberate
        // targets, and both sit ON it.
        if (data.kind === "note-anchor")
          return {
            kind: "anchor",
            noteId: data.noteId,
            anchorIndex: data.anchorIndex ?? 0,
          };
        if (data.kind === "note-badge")
          return { kind: "connect", noteId: data.noteId };
        if (!card) {
          // A corner of the card resizes it; anywhere else on it moves it.
          // Checked here rather than before the loop so it only applies to the
          // note actually under the cursor.
          const note = (this.ctx.history.state.notes ?? []).find(
            (n) => n.id === data.noteId,
          );
          const corner = note ? cornerAt(note, p, scope) : null;
          card = corner
            ? { kind: "resize", noteId: data.noteId, corner }
            : { kind: "move", noteId: data.noteId };
        }
        break;
      }
    }
    return card;
  }

  /** The cursor that says what a press here would do, or null over no note. */
  cursorOver(p: Vec2): string | null {
    const over = this.noteUnder(p);
    if (!over) return null;
    // The badge does something different from everything else on the card, so
    // it says so before the press: a pointing hand for "pull a line out of
    // here", a grab for "pick this tip up", a move cross for the card itself.
    if (over.kind === "connect") return "pointer";
    if (over.kind === "resize" && over.corner)
      return cursorForCorner(over.corner);
    return over.kind === "anchor" ? "grab" : "move";
  }

  /** Take the note under `p`, if there is one. True when a drag has begun and
   *  the host tool should stand down for the rest of the gesture. */
  begin(p: Vec2): boolean {
    const grabbed = this.noteUnder(p);
    if (!grabbed) return false;
    this.kind = grabbed.kind;
    this.noteId = grabbed.noteId;
    this.anchorIndex = grabbed.anchorIndex ?? 0;
    this.corner = grabbed.corner ?? null;
    this.resizeFrom = null;
    if (grabbed.kind === "resize") {
      const note = (this.ctx.history.state.notes ?? []).find(
        (n) => n.id === grabbed.noteId,
      );
      if (note) {
        this.resizeFrom = cardRect(note, this.ctx.scene.getPaperScope());
      }
    }
    this.press = { x: p.x, y: p.y };
    this.last = { x: p.x, y: p.y };
    this.original = this.ctx.history.state;
    // Double-click puts the cursor in the note's text box. Typing is what a
    // maker wants from a note they just went back to, and a double-click is
    // how every other text on a screen is opened for editing — armed BEFORE
    // the selection, because selecting is what renders the field that
    // consumes it.
    const now = Date.now();
    const again =
      this.lastPress?.noteId === grabbed.noteId &&
      now - this.lastPress.at < ROW_DBLCLICK_MS;
    this.lastPress = { noteId: grabbed.noteId, at: now };
    if (again) focusNoteTextOnNextRender(grabbed.noteId);
    this.ctx.setSelection({
      pieceId: null,
      decorationId: null,
      noteIds: [grabbed.noteId],
    });
    return true;
  }

  move(p: Vec2): boolean {
    if (!this.kind || !this.noteId || !this.last) return false;
    if (this.kind === "anchor") {
      this.previewReaim(this.noteId, this.anchorIndex, p);
      this.showTargetHighlight(p);
      return true;
    }
    if (this.kind === "resize") {
      this.previewResize(this.noteId, p);
      return true;
    }
    // Pulling a NEW line out of the badge: show where it would land, the same
    // dashed leader and real-size tip the create gesture shows.
    if (this.kind === "connect") {
      if (this.press) this.drawPreviewLeader(p, this.press);
      this.showTargetHighlight(p);
      return true;
    }
    const dx = p.x - this.last.x;
    const dy = p.y - this.last.y;
    this.last = { x: p.x, y: p.y };
    const id = this.noteId;

    // Preview through the MODEL rather than by translating the drawn group.
    //
    // Translating the items looked simpler and did not work: something
    // re-renders between pointer moves and rebuilds the note from the project,
    // discarding the translate — so the card appeared frozen and the leader and
    // numbered badge only settled on release.
    //
    // `history.replace` swaps the current project WITHOUT pushing an undo
    // entry, so each redraw is the real renderer working from real data and the
    // card, its leader and its badge stay consistent for free. The undo stack
    // still gets exactly one entry, pushed on release against `original`.
    this.ctx.history.replace({
      ...this.ctx.history.state,
      notes: (this.ctx.history.state.notes ?? []).map((n) =>
        n.id === id
          ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
          : n,
      ),
    });
    this.ctx.scene.render(this.ctx.history.state);
    return true;
  }

  /**
   * The box the drag is asking for: the held corner follows the cursor and its
   * opposite stays put (#1074).
   *
   * Measured from the box the drag STARTED on, not from the last frame, so a
   * slow drag cannot creep by accumulating rounding. Width and height are
   * floored at `MIN_NOTE_SIZE_MM` — a card dragged to nothing would be
   * unreachable afterwards, with no corner left to grab.
   */
  private resizedBox(p: Vec2): {
    position: Vec2;
    widthMm: number;
    heightMm: number;
  } | null {
    const from = this.resizeFrom;
    const corner = this.corner;
    if (!from || !corner) return null;
    const west = corner === "nw" || corner === "sw";
    const north = corner === "nw" || corner === "ne";
    const left = west
      ? Math.min(p.x, from.right - MIN_NOTE_SIZE_MM)
      : from.left;
    const right = west
      ? from.right
      : Math.max(p.x, from.left + MIN_NOTE_SIZE_MM);
    const top = north
      ? Math.min(p.y, from.bottom - MIN_NOTE_SIZE_MM)
      : from.top;
    const bottom = north
      ? from.bottom
      : Math.max(p.y, from.top + MIN_NOTE_SIZE_MM);
    return {
      position: { x: left, y: top },
      widthMm: right - left,
      heightMm: bottom - top,
    };
  }

  /** Live preview of a corner resize, through the model so the text re-wraps
   *  and the card re-grows under the drag rather than on release. */
  private previewResize(id: string, p: Vec2): void {
    const box = this.resizedBox(p);
    if (!box) return;
    this.ctx.history.replace({
      ...this.ctx.history.state,
      notes: (this.ctx.history.state.notes ?? []).map((n) =>
        n.id === id ? { ...n, ...box } : n,
      ),
    });
    this.ctx.scene.render(this.ctx.history.state);
  }

  /**
   * The tip follows the cursor so the maker can see what the callout will land
   * on, rather than finding out on release.
   *
   * Drawn by moving the items rather than through the model, because the model
   * stores an anchor as a position ALONG a part's outline — a tip halfway
   * across bare canvas has no such position to store, so there is nothing to
   * preview through.
   */
  private previewReaim(id: string, index: number, p: Vec2): void {
    const scope = this.ctx.scene.getPaperScope();
    for (const item of scope.project.getItems({ recursive: true })) {
      const d = item.data as
        | { kind?: string; noteId?: string; anchorIndex?: number }
        | undefined;
      if (d?.noteId !== id || (d.anchorIndex ?? 0) !== index) continue;
      if (d.kind === "note-anchor") {
        item.position = new scope.Point(p.x, p.y);
      } else if (d.kind === "note-callout") {
        const line = item as paper.Path;
        if (line.segments.length >= 2) {
          line.segments[0]!.point = new scope.Point(p.x, p.y);
        }
      }
    }
  }

  /** The dashed leader shown while a new callout is pulled out of the badge —
   *  drawn tip-first, since the tip is the end the maker is aiming. */
  private drawPreviewLeader(tip: Vec2, from: Vec2): void {
    const scope = this.ctx.scene.getPaperScope();
    if (!this.previewLine || !this.previewLine.parent) {
      const line = new scope.Path.Line({
        from: [tip.x, tip.y],
        to: [from.x, from.y],
        strokeColor: cssVar("--note-callout"),
        strokeWidth: 0.4,
        strokeScaling: false,
        dashArray: [2, 2],
      });
      // Ephemeral so paper's hit test skips it — it is feedback, never a
      // target, and it must not swallow the release.
      line.data = { kind: "note-callout-preview", ephemeral: true };
      this.previewLine = line;
      const dot = new scope.Path.Circle({
        center: [tip.x, tip.y],
        radius: ANCHOR_DOT_RADIUS_MM,
        fillColor: cssVar("--note-callout"),
      });
      dot.data = { kind: "note-anchor-preview", ephemeral: true };
      this.previewDot = dot;
      return;
    }
    this.previewLine.segments[0]!.point = new scope.Point(tip.x, tip.y);
    this.previewLine.segments[1]!.point = new scope.Point(from.x, from.y);
    if (this.previewDot) {
      this.previewDot.position = new scope.Point(tip.x, tip.y);
    }
  }

  private clearPreviewLeader(): void {
    this.previewLine?.remove();
    this.previewLine = null;
    this.previewDot?.remove();
    this.previewDot = null;
    this.clearTargetHighlight();
  }

  /**
   * Outline whatever the tip is over right now, so the maker sees what the
   * release will attach to BEFORE letting go (#1076).
   *
   * Necessary because the three things a callout can land on overlap: a seam
   * lies on its part, and a cutout is a window in it, so the cursor is over all
   * of them at once and only the app knows which one wins. Drawn as the target's
   * own outline in the leader's color — the same color says "this is the end of
   * the line you are dragging".
   *
   * Rebuilt only when the target CHANGES: re-parsing a path every pointer move
   * would be work for a picture that did not change, and the flicker would show.
   */
  private showTargetHighlight(p: Vec2): void {
    const target = this.targetAt(p);
    const key = target ? noteTargetKey(target) : null;
    // Also rebuild when the item has gone stale — something re-renders the
    // scene mid-drag in some configurations, taking the overlay with it.
    if (key === this.highlightKey && this.highlight?.parent) return;
    this.clearTargetHighlight();
    this.highlightKey = key;
    if (!target) return;
    const piece = this.ctx.history.state.pieces.find(
      (pc) => pc.id === target.pieceId,
    );
    if (!piece) return;
    const pathData = noteTargetPathData(piece, target);
    if (!pathData) return;
    const scope = this.ctx.scene.getPaperScope();
    const outline = new scope.CompoundPath({ pathData });
    outline.strokeColor = new scope.Color(cssVar("--note-callout"));
    outline.strokeWidth = 2.5;
    // Unscaled, so the mark stays the same weight at every zoom, and unfilled,
    // so it frames the object rather than hiding it.
    outline.strokeScaling = false;
    outline.fillColor = null;
    // Ephemeral so the hit test skips it — it is feedback, never a target, and
    // it sits directly under the cursor that is about to release.
    outline.data = { kind: "note-target-highlight", ephemeral: true };
    this.highlight = outline;
  }

  private clearTargetHighlight(): void {
    this.highlight?.remove();
    this.highlight = null;
    this.highlightKey = null;
  }

  end(p: Vec2): boolean {
    if (!this.kind || !this.noteId) return false;
    const kind = this.kind;
    const id = this.noteId;
    const index = this.anchorIndex;
    const press = this.press;
    const original = this.original;
    // Read the resize BEFORE resetting: the box is measured from state `reset`
    // is about to clear.
    const resized = kind === "resize" ? this.resizedBox(p) : null;
    this.clearPreviewLeader();
    this.reset();

    if (kind === "anchor") {
      this.reaimCallout(id, index, p);
      return true;
    }
    if (kind === "connect") {
      this.addCallout(id, p);
      return true;
    }
    if (kind === "resize") {
      // Rewind the preview first, for the same reason a move does: `commit`
      // snapshots the CURRENT project as the undo entry's "before".
      if (original) this.ctx.history.replace(original);
      if (!resized) {
        this.ctx.rerender();
        return true;
      }
      this.ctx.commit("Resize note", (project: Project) => ({
        ...project,
        notes: (project.notes ?? []).map((n) =>
          n.id === id ? { ...n, ...resized } : n,
        ),
      }));
      return true;
    }
    if (!press || !original) return true;
    const dx = p.x - press.x;
    const dy = p.y - press.y;
    // Rewind to before the preview first: `commit` snapshots the CURRENT
    // project as the undo entry's "before", and the current project is the
    // half-dragged one. Without this, one undo returns the note to the middle
    // of the drag instead of where it started.
    this.ctx.history.replace(original);
    if (dx === 0 && dy === 0) {
      // A press with no travel is a select; the rewind above already put the
      // project back, so just redraw from it.
      this.ctx.rerender();
      return true;
    }
    this.ctx.commit("Move note", (project: Project) => ({
      ...project,
      notes: (project.notes ?? []).map((n) =>
        n.id === id
          ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
          : n,
      ),
    }));
    return true;
  }

  /** Abandon a drag in flight, putting the note back where it was. */
  cancel(): boolean {
    this.clearPreviewLeader();
    if (!this.kind) return false;
    const original = this.original;
    this.reset();
    if (original) this.ctx.history.replace(original);
    this.ctx.rerender();
    return true;
  }

  private reset(): void {
    this.kind = null;
    this.noteId = null;
    this.corner = null;
    this.resizeFrom = null;
    this.last = null;
    this.press = null;
    this.original = null;
  }

  /**
   * What a drop at `p` would point at: the part under the cursor, refined to
   * the cutout or seam if the drop landed on one (#1076).
   *
   * Delegates to the shared leather pick rather than reading `Scene.hitTest`
   * directly, because the painted items lie about both of the finer targets and
   * in opposite directions. A seam paints only its chisel MARKS with nothing
   * between them, so hit-testing what is drawn means the maker has to land on
   * an individual hole rather than on the line they can plainly see; the pick
   * adds a grace distance from the seam's real path. A cutout in display mode
   * is a VOID — nothing is painted inside it, so a press in the window resolves
   * to no item at all; the pick falls back to geometric containment. Both traps
   * are the same ones `pickLeatherObjectAt` was written for (#990), and a
   * second copy is what that issue records the cost of.
   */
  private targetAt(p: Vec2): NoteTarget | null {
    const pick = pickLeatherObjectAt(
      this.ctx.scene,
      this.ctx.history.state,
      this.ctx.scene.getPaperScope(),
      p,
      { includeStitch: true },
    );
    if (!pick) return null;
    if (pick.kind === "stitch") {
      return { pieceId: pick.pieceId, stitchId: pick.stitchId };
    }
    if (pick.kind === "cutout") {
      return { pieceId: pick.pieceId, cutoutId: pick.cutoutId };
    }
    return { pieceId: pick.pieceId };
  }

  /**
   * Re-point a callout at wherever it was dragged to.
   *
   * Dropping the tip on a part re-aims it there — including a DIFFERENT part,
   * or one of its cutouts or seams, which is the natural way to fix a note
   * attached to the wrong thing. Dropping it on bare canvas detaches the note
   * entirely, since a leader pointing at nothing is worse than no leader.
   */
  private reaimCallout(id: string, index: number, p: Vec2): void {
    const scope = this.ctx.scene.getPaperScope();
    const target = this.targetAt(p);
    this.ctx.commit("Re-point note", (project: Project) => {
      const piece = target
        ? (project.pieces.find((pc) => pc.id === target.pieceId) ?? null)
        : null;
      const anchor =
        piece && target ? noteAnchorFor(piece, p, scope, target) : null;
      return {
        ...project,
        notes: (project.notes ?? []).map((n) => {
          if (n.id !== id) return n;
          const list = noteAnchors(n);
          // Dropped on bare canvas: THIS line goes, the note's others stay.
          if (!anchor) {
            return withNoteAnchors(
              n,
              list.filter((_, i) => i !== index),
            );
          }
          // Re-aimed onto something the note already points at: the two lines
          // would say the same thing, so the dragged one absorbs the other
          // rather than doubling up. Compared by TARGET, not by part — a note
          // on a part and a note on that part's seam are different lines.
          const key = noteTargetKey(noteAnchorTarget(anchor));
          const deduped = list.filter(
            (a, i) => i === index || noteTargetKey(noteAnchorTarget(a)) !== key,
          );
          return withNoteAnchors(
            n,
            deduped.map((a) => (list[index] === a ? anchor : a)),
          );
        }),
      };
    });
  }

  /**
   * A NEW callout, pulled out of the badge and dropped on a part, cutout or
   * seam.
   *
   * Dropped on bare canvas, or on something this note already points at,
   * nothing happens: the first has nothing to point at, and the second would
   * draw a second line saying exactly what the first one says.
   */
  private addCallout(id: string, p: Vec2): void {
    const scope = this.ctx.scene.getPaperScope();
    const target = this.targetAt(p);
    if (!target) {
      this.ctx.rerender();
      return;
    }
    const key = noteTargetKey(target);
    const note = (this.ctx.history.state.notes ?? []).find((n) => n.id === id);
    if (
      !note ||
      noteAnchors(note).some((a) => noteTargetKey(noteAnchorTarget(a)) === key)
    ) {
      this.ctx.rerender();
      return;
    }
    this.ctx.commit("Point note at part", (project: Project) => {
      const piece = project.pieces.find((pc) => pc.id === target.pieceId);
      const anchor = piece ? noteAnchorFor(piece, p, scope, target) : null;
      if (!anchor) return project;
      return {
        ...project,
        notes: (project.notes ?? []).map((n) =>
          n.id === id ? withNoteAnchors(n, [...noteAnchors(n), anchor]) : n,
        ),
      };
    });
  }
}

/**
 * Every callout tip pinned to one of `pieceIds`, with the position it is drawn
 * at right now (#157).
 *
 * Used to carry those tips along while the parts themselves are being dragged.
 * A note's anchor is stored as a position along its part's outline, so the
 * COMMITTED drawing puts the tip back in the right place on its own; what this
 * covers is the transient, where the leather has been translated on the canvas
 * but nothing has been written to the model yet, and a tip left behind would
 * read as the note having come unstuck.
 *
 * The CARD is deliberately not included: it sits where the maker parked it,
 * which is a decision about the layout of the sheet, not about the part.
 *
 * Identified by note AND callout index, not by note: a note can point at
 * several parts, so "the tip of note 3" is not a thing — "the tip of note 3's
 * second callout" is. Keying by note alone kept whichever callout the scan
 * reached last, so dragging one part moved another part's line.
 */
export function noteTipsForPieces(
  scope: paper.PaperScope,
  project: Project,
  pieceIds: readonly string[],
): NoteTipRef[] {
  const ids = new Set(pieceIds);
  const out: NoteTipRef[] = [];
  for (const note of project.notes ?? []) {
    noteAnchors(note).forEach((a, index) => {
      // Only the callouts pinned to a part in THIS drag: a note spanning a
      // dragged part and a stationary one keeps its other line where it is.
      if (!ids.has(a.pieceId)) return;
      const origin = resolveNoteAnchor(a, project, scope);
      if (origin) out.push({ noteId: note.id, index, origin, note });
    });
  }
  return out;
}

export interface NoteTipRef {
  noteId: string;
  index: number;
  /** Where the tip sits in the committed project — the base every frame's
   *  offset is measured from. */
  origin: Vec2;
  /** The note itself, so the leader's CARD end — and the badge that rides it —
   *  can be re-derived from the moved tip. The card does not move during a part
   *  drag, but where the line MEETS it does: the landing point is the tip
   *  clamped into the card's rectangle (#1078). */
  note: Note;
}

/**
 * Put each dragged tip at `origin + (dx, dy)`, where the drag has got to.
 *
 * Absolute, and re-found from the scene every frame, rather than translating
 * the items by the frame's delta. Two reasons, both learned the hard way:
 * something re-renders the notes mid-drag in some configurations, which both
 * REPLACES the items (so a held reference goes stale and silently stops
 * moving) and redraws them from a live project (so a translate on top moves
 * them twice). Setting an absolute position from the committed origin is
 * right either way.
 */
export function applyNoteTipOffsets(
  scope: paper.PaperScope,
  tips: readonly NoteTipRef[],
  dx: number,
  dy: number,
): void {
  if (tips.length === 0) return;
  const byKey = new Map(tips.map((t) => [`${t.noteId}:${t.index}`, t]));
  for (const item of scope.project.getItems({ recursive: true })) {
    const d = item.data as
      | { kind?: string; noteId?: string; anchorIndex?: number }
      | undefined;
    if (!d?.noteId) continue;
    const tip = byKey.get(`${d.noteId}:${d.anchorIndex ?? 0}`);
    if (!tip) continue;
    const at = new scope.Point(tip.origin.x + dx, tip.origin.y + dy);
    if (d.kind === "note-anchor") {
      item.position = at;
      continue;
    }
    // Where the line meets the card moves too — it is the tip clamped into the
    // card's rectangle, so a tip travelling with the leather slides it along
    // the card edge (#1078). The badge and its number ride that point.
    const landing = calloutLandingPoint(tip.note, { x: at.x, y: at.y });
    const meets = new scope.Point(landing.x, landing.y);
    if (d.kind === "note-callout") {
      const line = item as paper.Path;
      if (line.segments.length >= 2) {
        line.segments[0]!.point = at;
        line.segments[1]!.point = meets;
      }
    } else if (d.kind === "note-badge") {
      item.position = meets;
    } else if (d.kind === "note-badge-label") {
      // Placed by its BASELINE, the same handle the renderer used — setting a
      // PointText's bounds centre instead would drift it by the cap height.
      (item as paper.PointText).point = meets.add(
        new scope.Point(0, BADGE_LABEL_DY_MM),
      );
    }
  }
}
